import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Controller,
  FormProvider,
  SubmitHandler,
  useForm,
} from "react-hook-form";

import { Button, InputText, InputTextArea, Select, Text } from "@pentabd/ui";

import {
  ErrorModal,
  InputDateController,
  MultiFile,
  SuccessModal,
} from "@components";

import { FORM_FIELDS } from "@constants/form";
import { yupResolver } from "@hookform/resolvers/yup";

import {
  SubTaskDetailsFormDataType,
  subTaskDetailsValidation,
  SUB_TASK_DETAILS,
} from "@validations/TaskManagement/subTaskDetailsValidation";

import { SelectEmployee } from "@components";

import useActionCreator from "@redux/hooks/useActionCreator";

import { SingleSubTasksResponseType } from "@type/taskManagement/subTasks";
import { useSubTasksById } from "@hooks/taskManagement/subTasks/useSubTasksById";
import { PriorityOptions, StatusOptions } from "@type/taskManagement/common";
import { useStatusList } from "@hooks/taskManagement/settings/status/useStatusList";
import { StatusResponseType } from "@type/taskManagement/settings/status";
import { usePriorityList } from "@hooks/taskManagement/settings/priority/usePriorityList";
import { PriorityResponseType } from "@type/taskManagement/settings/priority";
import {
  convertMultipleDocumentData,
  revertMultipleDocumentData,
} from "@utils/documentDataConvert";

const GENERAL_INFO = FORM_FIELDS.EMPLOYEE.GENERAL_INFO;

export const SubTaskDetailsForm = ({
  id,
  id2,
}: {
  id: string | number | undefined;
  id2: string | number | undefined;
}) => {
  const { t } = useTranslation();

  const [statusOptions, setStatusOptions] = useState<StatusOptions>([]);
  const [priorityOptions, setPriorityOptions] = useState<PriorityOptions>([]);

  const { statusState } = useStatusList();
  const { getData: statusGetData } = statusState;
  const { data: statusData } = statusGetData || ({} as StatusResponseType);

  const { priorityState } = usePriorityList();
  const { getData: priorityGetData } = priorityState;

  const { data: priorityData } =
    priorityGetData || ({} as PriorityResponseType);

  const {
    ASSIGNED_PERSON,
    SHARED_PERSON,
    NAME_OF_SUB_TASK,
    DETAILS,
    START_DATE,
    END_DATE,
    PREREQUISITES,
    PRIORITY,
    STATUS,
    REMARKS,
    ATTACHMENTS,
  } = SUB_TASK_DETAILS;

  const navigate = useNavigate();

  const { subTasksState } = useSubTasksById();

  const {
    createSuccess,
    updateSuccess,
    createError,
    updateError,
    getSingleData,
  } = subTasksState;

  const { responsiblePersons, sharedWith, priorityId, statusId } =
    getSingleData || ({} as SingleSubTasksResponseType);

  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState<boolean>(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState<boolean>(false);

  const [successSubtitle, setSuccessSubtitle] = useState<string>("");

  const { subTasksCreateActionHandler, subTasksUpdateActionHandler } =
    useActionCreator();

  const methods = useForm<SubTaskDetailsFormDataType>({
    resolver: yupResolver(subTaskDetailsValidation),
    values: id2 ? revertMultipleDocumentData(getSingleData, [ATTACHMENTS]) : {},
  });

  const {
    handleSubmit,
    register,
    control,
    reset,
    formState: { errors },
    setValue,
  } = methods;

  const successAction = () => {
    reset();
    setIsSuccessModalOpen(false);
    navigate(-1);
  };

  const closeErrorModal = () => {
    setIsErrorModalOpen(false);
  };

  const onSubmit: SubmitHandler<SubTaskDetailsFormDataType> = (data: any) => {
    for (var key in data) {
      if (
        key === `${GENERAL_INFO.DESIGNATION}-${ASSIGNED_PERSON}` ||
        key === `${GENERAL_INFO.DESIGNATION}-${SHARED_PERSON}` ||
        key === `${GENERAL_INFO.WING_DESK_IO_E0}-${ASSIGNED_PERSON}` ||
        key === `${GENERAL_INFO.WING_DESK_IO_E0}-${SHARED_PERSON}`
      ) {
        delete data[key];
      }
    }

    const formattedData = {
      ...data,
      documents: data?.documents || [],
      responsiblePersons: data?.responsiblePersons
        ? [Number(data.responsiblePersons)]
        : [],
      sharedWith: data?.sharedWith || [],
      status: Number(data?.status),
      priority: data?.priority ? Number(data?.priority) : null,
    };

    if (id2) {
      subTasksUpdateActionHandler(
        id as string,
        id2,
        convertMultipleDocumentData(formattedData, [ATTACHMENTS]),
      );
    } else {
      subTasksCreateActionHandler(
        id as string,
        convertMultipleDocumentData(formattedData, [ATTACHMENTS]),
      );
    }
  };

  useEffect(() => {
    if (statusData) {
      const status = statusData.map((item: any) => ({
        label: item?.name || "",
        value: `${item?.id}`,
      }));

      setStatusOptions((prev) => status);
    }
  }, [statusGetData]);

  useEffect(() => {
    if (priorityData) {
      const priority = priorityData.map((item: any) => ({
        label: item?.name || "",
        value: `${item?.id}`,
      }));

      setPriorityOptions((prev) => priority);
    }
  }, [priorityGetData]);

  useEffect(() => {
    if (getSingleData) {
      const sharedWithId = sharedWith?.map((item: any) => item?.id);

      setValue(PRIORITY, priorityId ? `${priorityId}` : "");
      setValue(STATUS, `${statusId}`);

      setValue(
        `${GENERAL_INFO.WING_DESK_IO_E0}-${ASSIGNED_PERSON}`,
        responsiblePersons[0] ? responsiblePersons[0].unitId : null,
      );

      setValue(
        `${GENERAL_INFO.DESIGNATION}-${ASSIGNED_PERSON}`,
        responsiblePersons[0] ? responsiblePersons[0].positionId : null,
      );

      setValue(
        ASSIGNED_PERSON,
        responsiblePersons[0] ? responsiblePersons[0].id : null,
      );

      setValue(
        `${GENERAL_INFO.WING_DESK_IO_E0}-${SHARED_PERSON}`,
        sharedWith[0] ? sharedWith[0].unitId : null,
      );

      setValue(
        `${GENERAL_INFO.DESIGNATION}-${SHARED_PERSON}`,
        sharedWith[0] ? sharedWith[0].positionId : null,
      );

      setValue(SHARED_PERSON, sharedWithId);
    }
  }, [getSingleData]);

  useEffect(() => {
    if (createSuccess) {
      setSuccessSubtitle("You have created a new sub task");
      setIsSuccessModalOpen(true);
    }

    if (updateSuccess) {
      setSuccessSubtitle("You have updated a sub task");
      setIsSuccessModalOpen(true);
    }

    if (createError || updateError) {
      setSuccessSubtitle("");
      setIsErrorModalOpen(true);
    }
  }, [createSuccess, createError, updateSuccess, updateError]);

  useEffect(() => {
    reset();

    setValue(`${GENERAL_INFO.WING_DESK_IO_E0}-${ASSIGNED_PERSON}`, null);
    setValue(`${GENERAL_INFO.DESIGNATION}-${ASSIGNED_PERSON}`, null);
    setValue(`${ASSIGNED_PERSON}`, null);
    setValue(`${GENERAL_INFO.WING_DESK_IO_E0}-${SHARED_PERSON}`, null);
    setValue(`${GENERAL_INFO.DESIGNATION}-${SHARED_PERSON}`, null);
    setValue(`${SHARED_PERSON}`, null);
  }, []);

  return (
    <>
      <FormProvider {...methods}>
        <form className="container" onSubmit={handleSubmit(onSubmit)}>
          <div className="d-flex flex-column gap-8 mb-8 pb-8 border-bottom">
            <div className="row">
              <div className="col-md-12 col-lg-3">
                <Text weight="semibold" size="sm" color="title">
                  {t("TM_SUB_TASKS.TASKS_CREATE.FORM.NAME_OF_SUB_TASK.LABEL")}
                </Text>
                <span className="text-danger">&#42;</span>
              </div>
              <div className="col-md-12 col-lg-7">
                <InputText
                  {...register(NAME_OF_SUB_TASK)}
                  placeholder={t(
                    "TM_SUB_TASKS.TASKS_CREATE.FORM.NAME_OF_SUB_TASK.PLACEHOLDER",
                  )}
                  className="fs-md"
                  error={errors as any}
                />
              </div>
            </div>

            <div className="row">
              <div className="col-md-12 col-lg-3">
                <Text weight="semibold" size="sm" color="title">
                  {t("TM_SUB_TASKS.TASKS_CREATE.FORM.DETAILS.LABEL")}
                </Text>
              </div>
              <div className="col-md-12 col-lg-7">
                <InputTextArea
                  {...register(DETAILS)}
                  placeholder={t(
                    "TM_SUB_TASKS.TASKS_CREATE.FORM.DETAILS.PLACEHOLDER",
                  )}
                  error={errors as any}
                />
              </div>
            </div>

            <div className="row">
              <div className="col-md-12 col-lg-3">
                <Text weight="semibold" size="sm" color="title">
                  {t("TM_SUB_TASKS.TASKS_CREATE.FORM.START_DATE.LABEL")}
                </Text>
                <span className="text-danger">&#42;</span>
              </div>
              <div className="col-md-12 col-lg-7">
                <InputDateController
                  control={control}
                  errors={errors as any}
                  name={START_DATE}
                  placeholder={t(
                    "TM_SUB_TASKS.TASKS_CREATE.FORM.START_DATE.PLACEHOLDER",
                  )}
                />
              </div>
            </div>

            <div className="row">
              <div className="col-md-12 col-lg-3">
                <Text weight="semibold" size="sm" color="title">
                  {t("TM_SUB_TASKS.TASKS_CREATE.FORM.END_DATE.LABEL")}
                </Text>
                <span className="text-danger">&#42;</span>
              </div>
              <div className="col-md-12 col-lg-7">
                <InputDateController
                  control={control}
                  errors={errors as any}
                  name={END_DATE}
                  placeholder={t(
                    "TM_SUB_TASKS.TASKS_CREATE.FORM.END_DATE.PLACEHOLDER",
                  )}
                />
              </div>
            </div>

            <div className="row">
              <div className="col-md-12 col-lg-3">
                <Text weight="semibold" size="sm" color="title">
                  {t("TM_SUB_TASKS.TASKS_CREATE.FORM.PREREQUISITES.LABEL")}
                </Text>
              </div>
              <div className="col-md-12 col-lg-7">
                <InputTextArea
                  {...register(PREREQUISITES)}
                  placeholder={t(
                    "TM_SUB_TASKS.TASKS_CREATE.FORM.PREREQUISITES.PLACEHOLDER",
                  )}
                  error={errors as any}
                />
              </div>
            </div>

            <div className="row">
              <div className="col-md-12 col-lg-3">
                <Text weight="semibold" size="sm" color="title">
                  {t("TM_SUB_TASKS.TASKS_CREATE.FORM.ASSIGNED_PERSON.LABEL")}
                </Text>
              </div>
              <div className="col-md-12 col-lg-7">
                <SelectEmployee
                  registeredName={ASSIGNED_PERSON}
                  selectSingleEmployee={true}
                />
              </div>
            </div>

            <div className="row">
              <div className="col-md-12 col-lg-3">
                <Text weight="semibold" size="sm" color="title">
                  {t("TM_SUB_TASKS.TASKS_CREATE.FORM.SHARED_PERSON.LABEL")}
                </Text>
              </div>
              <div className="col-md-12 col-lg-7">
                <SelectEmployee registeredName={SHARED_PERSON} />
              </div>
            </div>

            <div className="row">
              <div className="col-md-12 col-lg-3">
                <Text weight="semibold" size="sm" color="title">
                  {t("TM_SUB_TASKS.TASKS_CREATE.FORM.PRIORITY.LABEL")}
                </Text>
              </div>
              <div className="col-md-12 col-lg-7">
                <Controller
                  control={control}
                  name={PRIORITY}
                  render={({ field }) => {
                    return (
                      <Select
                        {...register(`${PRIORITY}`)}
                        options={priorityOptions}
                        defaultValue={field.value as string}
                        placeholder={t(
                          "TM_SUB_TASKS.TASKS_CREATE.FORM.PRIORITY.PLACEHOLDER",
                        )}
                        onSelectItem={(e) => {
                          setValue(PRIORITY, e as string);
                        }}
                      />
                    );
                  }}
                />
                {errors[`${PRIORITY}`] && (
                  <Text
                    color="danger"
                    component="p"
                    size="sm"
                    weight="lighter"
                    className="mt-2"
                  >
                    {errors[`${PRIORITY}`]?.message || ""}
                  </Text>
                )}
              </div>
            </div>

            <div className="row">
              <div className="col-md-12 col-lg-3">
                <Text weight="semibold" size="sm" color="title">
                  {t("TM_SUB_TASKS.TASKS_CREATE.FORM.STATUS.LABEL")}
                </Text>
                <span className="text-danger">&#42;</span>
              </div>
              <div className="col-md-12 col-lg-7">
                <Controller
                  control={control}
                  name={STATUS}
                  render={({ field }) => {
                    return (
                      <Select
                        {...register(`${STATUS}`)}
                        options={statusOptions}
                        defaultValue={field.value as string}
                        placeholder={t(
                          "TM_SUB_TASKS.TASKS_CREATE.FORM.STATUS.PLACEHOLDER",
                        )}
                        onSelectItem={(e) => {
                          setValue(STATUS, e as string);
                        }}
                      />
                    );
                  }}
                />
                {errors[`${STATUS}`] && (
                  <Text
                    color="danger"
                    component="p"
                    size="sm"
                    weight="lighter"
                    className="mt-2"
                  >
                    {errors[`${STATUS}`]?.message || ""}
                  </Text>
                )}
              </div>
            </div>

            <div className="row">
              <div className="col-md-12 col-lg-3">
                <Text weight="semibold" size="sm" color="title">
                  {t("TM_SUB_TASKS.TASKS_CREATE.FORM.REMARKS.LABEL")}
                </Text>
              </div>
              <div className="col-md-12 col-lg-7">
                <InputText
                  {...register(REMARKS)}
                  placeholder={t(
                    "TM_SUB_TASKS.TASKS_CREATE.FORM.REMARKS.PLACEHOLDER",
                  )}
                  className="fs-md"
                  error={errors as any}
                />
              </div>
            </div>

            <div className="row">
              <div className="col-md-12 col-lg-3">
                <Text weight="semibold" size="sm" color="title">
                  {t("TM_SUB_TASKS.TASKS_CREATE.FORM.ATTACHMENTS")}
                </Text>
              </div>
              <div className="col-md-12 col-lg-7">
                <MultiFile
                  id={ATTACHMENTS}
                  control={control}
                  setValue={setValue}
                  register={register}
                  errors={errors}
                  fileDetails={["memorialNo"]}
                />
              </div>
            </div>
          </div>

          <div className="row py-10 justify-content-end">
            <div className="text-end col-12 col-lg-10">
              <div className="d-flex gap-8 flex-row-reverse">
                <Button
                  key="task_save_bottom"
                  htmlType="submit"
                  fill="fill"
                  type="primary"
                  size="sm"
                >
                  {t("TM_SUB_TASKS.TASKS_CREATE.FORM.SAVE_BUTTON")}
                </Button>
                <Button htmlType="button" type="light" fill="outline" size="sm">
                  {t("TM_SUB_TASKS.TASKS_CREATE.FORM.CANCEL_BUTTON")}
                </Button>
              </div>
            </div>
          </div>
        </form>
      </FormProvider>

      {/* modals  */}
      {isSuccessModalOpen && (
        <SuccessModal
          isModalOpen={isSuccessModalOpen}
          action={successAction}
          subtitle={successSubtitle}
        />
      )}

      {isErrorModalOpen && (
        <ErrorModal
          isModalOpen={isErrorModalOpen}
          closeModal={closeErrorModal}
        />
      )}
    </>
  );
};
